import styled from '@emotion/styled';
import { Button, Input, Modal, snack } from '@innovamat/glimmer-components';

import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../user-management';
import { SuccessMessage } from '../../components/success-message';
import TwoColumnLayout from '../../components/two-columns-layout/two-columns-layout';
import { useLanguage } from '../../hooks/use-language';
import { useRefreshResetPasswordToken } from '../../hooks/use-reset-password/use-refresh-reset-password-token';
import { useResetPassword } from '../../hooks/use-reset-password/use-reset-password';

type ErrorData = {
  type: string;
  detail: string;
};

const ErrorMessage = styled.div`
  > a {
    cursor: pointer;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

function ResetPassword() {
  const { t, i18n } = useTranslation();
  const { setHtmlLanguage } = useLanguage();
  const [, forceUpdate] = useState({});

  useEffect(() => {
    setHtmlLanguage(i18n.language);
    forceUpdate({});
  }, [i18n.language]);

  const navigate = useNavigate();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { refreshResetPasswordToken } = useRefreshResetPasswordToken();
  const { resetPassword, isSuccess, error, isPending } = useResetPassword();

  const { isLoggedIn, onClearUserSession } = useUser();

  const [showPassword, setShowPassword] = useState(false);

  const [credentials, setCredentials] = useState({
    password: '',
    confirmPassword: '',
  });

  const url = new URL(window.location.href);
  const requestId = url.searchParams.get('id');
  const recoveryToken = url.searchParams.get('token');

  const handleChangeInput = (value: string, name: string) => {
    setCredentials((prevVal) => ({
      ...prevVal,
      [name]: value,
    }));
  };

  const handleSendNewRequest = () => {
    if (!requestId || !recoveryToken) return;
    refreshResetPasswordToken(
      { requestId, token: recoveryToken },
      {
        onSuccess: () => navigate('/'),
        onError: (error) => {
          const errorData = error?.response?.data as ErrorData | undefined;
          snack.error(errorData?.detail);
        },
      }
    );
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    resetPassword({
      requestId: requestId!,
      password,
      token: recoveryToken!,
    });
  };

  useEffect(() => {
    if (isLoggedIn) {
      onClearUserSession();
    }
  }, [isLoggedIn]);

  const errorData = error?.response?.data as ErrorData | undefined;

  const attemptsError =
    error?.response?.status === 409 &&
    errorData?.type === 'password_request.verification_attempts_exceeded';

  const expiredError =
    error?.response?.status === 410 &&
    errorData?.type === 'password_request.expired';

  useEffect(() => {
    if (attemptsError || expiredError) {
      setShowErrorModal(true);
    }
  }, [error]);

  const { confirmPassword, password } = credentials;

  const validatePassword = () =>
    !!(password && confirmPassword && password === confirmPassword);

  const getErrorMessage = () => {
    if (error?.response?.status === 400) {
      return t(
        'Recovery.La contraseña introducida no cumple la política de seguridad.'
      );
    }
    return t(
      'Recovery.Ha ocurrido un error al crear la contraseña. Por favor, vuelve a intentarlo.'
    );
  };

  return (
    <>
      <TwoColumnLayout
        isSuccess={isSuccess}
        onSuccessBody={
          <SuccessMessage
            title={t('Contraseña modificada correctamente.')}
            body={t('Para acceder haz clic en el siguiente enlace:')}
            action={() => navigate('/')}
            actionText={t('Login.Accedir')}
          />
        }
        canGoBack
        error={error ? getErrorMessage() : undefined}
        title={t('Login.Introduce tu nueva contraseña')}
        goBackId="goBackForgotPassword"
        dropdownId="forgotPasswordDropdownLang"
      >
        <Form action="" onSubmit={handleSave}>
          <Input
            labelText={t('Login.Contrassenya')}
            required
            descriptiveText={t(
              'Recovery.(Mínimo 6 caracteres, combinando letras y números)'
            )}
            type={showPassword ? 'text' : 'password'}
            value={password}
            placeholder={t('Login.ContrassenyaPlaceholder')}
            onChange={(e) => handleChangeInput(e.target.value, 'password')}
            rightIcon={showPassword ? 'ViewIcon' : 'ViewOffIcon'}
            onIconClick={() => setShowPassword((prevState) => !prevState)}
            data-testid="resetPasswordInputPassword"
          />
          <Input
            descriptiveText={t('Recovery.Confirmar Contrassenya')}
            required
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            placeholder={t('Login.ContrassenyaPlaceholder')}
            onChange={(e) =>
              handleChangeInput(e.target.value, 'confirmPassword')
            }
            rightIcon={showPassword ? 'ViewIcon' : 'ViewOffIcon'}
            onIconClick={() => setShowPassword((prevState) => !prevState)}
            data-testid="resetPasswordInputConfirmPassword"
          />
          <Button
            fill
            disabled={!validatePassword()}
            onClick={handleSave}
            loading={isPending}
            data-testid="resetPasswordButtonSave"
          >
            {t('Settings.Guardar')}
          </Button>
        </Form>
      </TwoColumnLayout>
      <Modal
        isOpen={showErrorModal}
        onSubmit={() => setShowErrorModal(false)}
        onClose={() => setShowErrorModal(false)}
      >
        <>
          {attemptsError && (
            <ErrorMessage>
              <Trans
                i18nKey="password_request.verification_attempts_exceeded"
                components={{
                  clickhere: <a onClick={handleSendNewRequest} />,
                  underline: <u />,
                }}
              />
            </ErrorMessage>
          )}
          {expiredError && (
            <ErrorMessage>
              <Trans
                i18nKey="password_request.expired"
                components={{
                  clickhere: <a onClick={handleSendNewRequest} />,
                  underline: <u />,
                }}
              />
            </ErrorMessage>
          )}
        </>
      </Modal>
    </>
  );
}

export { ResetPassword };
